import React, { useState, useEffect } from 'react';
import { X, RotateCcw, Sparkles, Check } from 'lucide-react';
import { formatClockTime } from '../utils/units';

type DepartureTimelineProps = {
  departureTime: Date;
  onConfirm: (date: Date) => void;
  onPreviewChange?: (date: Date) => void;
  onClose: () => void;
  bestLeave?: Date | null;
  inline?: boolean;
};

export const DepartureTimeline: React.FC<DepartureTimelineProps> = ({
  departureTime,
  onConfirm,
  onPreviewChange,
  onClose,
  bestLeave = null,
  inline = false,
}) => {
  const [selectedTime, setSelectedTime] = useState<Date>(() => new Date(departureTime));
  const now = new Date();

  // Minutes from now: 0 to 7 * 24 * 60 = 10080
  const currentDiffMin = Math.max(
    0,
    Math.round((selectedTime.getTime() - now.getTime()) / 60_000),
  );

  const [sliderMinutes, setSliderMinutes] = useState(currentDiffMin);

  useEffect(() => {
    onPreviewChange?.(selectedTime);
  }, [selectedTime, onPreviewChange]);

  const updateTimeFromMinutes = (mins: number) => {
    const clamped = Math.max(0, Math.min(10080, mins));
    setSliderMinutes(clamped);
    const newDate = new Date(now.getTime() + clamped * 60_000);
    setSelectedTime(newDate);
    onPreviewChange?.(newDate);
  };

  const isNow = sliderMinutes <= 2;

  // Day indicator
  const getDayLabel = (d: Date) => {
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTarget = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86_400_000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatRelativeTime = (mins: number) => {
    if (mins <= 2) return 'Leaving Now';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `In ${m} min`;
    if (m === 0) return `In ${h}h`;
    return `In ${h}h ${m}m`;
  };

  const handleDone = () => {
    onConfirm(selectedTime);
    onClose();
  };

  return (
    <div
      id="in-place-departure-timeline"
      className="sheet-surface"
      style={{
        position: inline ? 'relative' : 'fixed',
        bottom: inline ? 'auto' : 24,
        left: inline ? 'auto' : '50%',
        transform: inline ? 'none' : 'translateX(-50%)',
        width: inline ? '100%' : 'calc(100% - 32px)',
        maxWidth: 500,
        borderRadius: 24,
        padding: '16px 20px',
        boxSizing: 'border-box',
        color: 'var(--label)',
        zIndex: 850,
        boxShadow: inline
          ? 'none'
          : '0 12px 36px rgba(0,0,0,0.3), 0 0 0 1px var(--sep)',
        pointerEvents: 'auto',
      }}
    >
      {/* Top Header Row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div className="t-headline" style={{ fontSize: 15, fontWeight: 700 }}>
            Live Departure Scrub
          </div>
          <div className="t-foot c2" style={{ fontSize: 12 }}>
            Map route weather updates live in-place
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => updateTimeFromMinutes(0)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              height: 28,
              padding: '0 10px',
              borderRadius: 14,
              border: 'none',
              background: isNow ? 'var(--accent)' : 'var(--fill2)',
              color: isNow ? '#FFF' : 'var(--accent)',
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
            }}
            title="Reset to current time"
          >
            <RotateCcw size={12} />
            <span>Now</span>
          </button>

          <button
            type="button"
            className="cbtn"
            style={{ width: 28, height: 28 }}
            onClick={handleDone}
            aria-label="Confirm and close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Primary Departure Readout Box */}
      <div
        className="card-surface"
        style={{
          padding: '12px 16px',
          marginBottom: 14,
          borderRadius: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div className="t-cap2 c2" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {getDayLabel(selectedTime)} · {formatRelativeTime(sliderMinutes)}
          </div>
          <div className="t-large" style={{ color: 'var(--accent)', fontSize: 24, fontWeight: 700 }}>
            {formatClockTime(selectedTime, true)}
          </div>
        </div>

        {bestLeave && (
          <button
            type="button"
            onClick={() => {
              const diff = Math.max(0, Math.round((bestLeave.getTime() - now.getTime()) / 60_000));
              updateTimeFromMinutes(diff);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 12px',
              borderRadius: 16,
              background: 'rgba(52, 199, 89, 0.15)',
              color: '#34C759',
              border: '1px solid rgba(52, 199, 89, 0.3)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            title="Switch to best departure time for less rain"
          >
            <Sparkles size={13} />
            <span>Best {formatClockTime(bestLeave, true)}</span>
          </button>
        )}
      </div>

      {/* 7-Day Continuous Scrub Slider */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="range"
          min={0}
          max={7 * 24 * 60}
          step={15}
          value={sliderMinutes}
          onInput={(e) => updateTimeFromMinutes(Number((e.target as HTMLInputElement).value))}
          onChange={(e) => updateTimeFromMinutes(Number(e.target.value))}
          style={{
            width: '100%',
            height: 24,
            cursor: 'ew-resize',
            accentColor: 'var(--accent)',
            margin: 0,
          }}
          aria-label="Departure timeline slider up to 7 days"
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 4,
            fontSize: 11,
          }}
          className="t-cap2 c3"
        >
          <span style={{ fontWeight: sliderMinutes === 0 ? 700 : 400 }}>Now</span>
          <span style={{ fontWeight: sliderMinutes >= 1440 && sliderMinutes < 2880 ? 700 : 400 }}>+24h</span>
          <span style={{ fontWeight: sliderMinutes >= 4320 && sliderMinutes < 5760 ? 700 : 400 }}>+3 Days</span>
          <span style={{ fontWeight: sliderMinutes >= 7200 && sliderMinutes < 8640 ? 700 : 400 }}>+5 Days</span>
          <span style={{ fontWeight: sliderMinutes >= 10080 ? 700 : 400 }}>+7 Days</span>
        </div>
      </div>

      {/* Bottom Done Action */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handleDone}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 32,
            padding: '0 16px',
            borderRadius: 16,
            background: 'var(--accent)',
            color: '#FFFFFF',
            border: 'none',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Check size={14} />
          <span>Done</span>
        </button>
      </div>
    </div>
  );
};
